// Базовые оптические примитивы, на которых стоят все 15 калькуляторов.
//
// Слой — чистые функции: ни React, ни i18n, ни форматирования. На вход и выход
// только числа и простые структуры; всё, что зависит от языка (направления,
// вердикты), возвращается кодом-перечислением, а текст подбирает UI.
//
// Источник — `starling-vision-prototype.jsx` (блок «Optics math helpers»).
// Осознанные отклонения от прототипа помечены блоками «ОТКЛОНЕНИЕ ОТ ПРОТОТИПА»
// и продублированы в накопителе V1 — молча математику не правим.

/** Рецепт: сфера/цилиндр в диоптриях, ось в градусах. */
export interface Rx {
  sphere: number;
  cyl: number;
  axis: number;
}

/** Оптический вектор Тибо (Thibos): сфероэквивалент + две цилиндрические компоненты. */
export interface PowerVector {
  M: number;
  J0: number;
  J45: number;
}

/** Толщина линзы: по центру, по краю и стрелка прогиба — всё в мм. */
export interface LensThickness {
  center: number;
  edge: number;
  sag: number;
}

/**
 * Стрелка прогиба (sagitta) сферической поверхности, мм.
 *
 * R = (n − 1) · 1000 / |F| — радиус поверхности, мм; sag ≈ r² / (2R) —
 * параболическое приближение (точная формула R − √(R² − r²) даёт чуть больше).
 * Нулевая сила — плоская поверхность, прогиба нет.
 */
export function sagFromPower(diameterMm: number, powerD: number, index: number): number {
  const power = Math.abs(powerD);
  if (!power || !Number.isFinite(power)) return 0;
  const radiusMm = ((index - 1) * 1000) / power;
  // Индекс ≤ 1 физически невозможен (в прототипе дал бы Infinity) — считаем плоской.
  if (!Number.isFinite(radiusMm) || radiusMm <= 0) return 0;
  const semiDiameter = diameterMm / 2;
  return (semiDiameter * semiDiameter) / (2 * radiusMm);
}

/**
 * Толщина по центру и по краю от заданной опорной толщины.
 *
 * Для минусовой линзы опорная — толщина по ЦЕНТРУ, край толще на стрелку прогиба.
 * Для плюсовой — опорная толщина по КРАЮ, центр толще на ту же стрелку.
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D1). Прототип для плюса считал
 * `center = max(ref − sag, 0.6)`, то есть делал центр плюсовой линзы ТОНЬШЕ
 * края: для d=60, +4.00 дптр, n=1.60, край 1.2 мм он выдавал центр 0.6 мм
 * вместо 4.2 мм. Это ошибка знака (в минусовой ветке того же хелпера прогиб
 * прибавляется правильно). Исправлено; заодно исчезла нужда в ограничителе 0.6.
 */
export function edgeCenterThickness(
  diameterMm: number,
  powerD: number,
  index: number,
  refThicknessMm: number,
): LensThickness {
  const sag = sagFromPower(diameterMm, powerD, index);
  if (powerD < 0) {
    return { center: refThicknessMm, edge: refThicknessMm + sag, sag };
  }
  return { center: refThicknessMm + sag, edge: refThicknessMm, sag };
}

/**
 * Децентрация линзы на глаз, мм: Dec = (A + DBL) / 2 − PD_моно.
 *
 * Знак: «+» — оптический центр уводится к виску (temporal), «−» — к переносице
 * (nasal). PD здесь МОНОКУЛЯРНЫЙ; для бинокулярного PD вызывающий передаёт
 * `pd / 2` (см. `blankSize`, `validateRx`).
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D6). В прототипе один хелпер
 * `decentrationCalc(A, DBL, PD) = (A + DBL − PD) / 2` вызывался с ДВУМЯ разными
 * соглашениями о PD: с бинокулярным (калькуляторы «Диаметр заготовки» и
 * «Проверка заказа» — там он верен) и с монокулярным («Децентрация и призма» —
 * там он завышал результат вдвое минус половину полудистанции: 19.5 мм вместо
 * 4.0 мм на дефолтных значениях прототипа, и призму 5.85 Δ вместо 1.20 Δ).
 * Собственное примечание прототипа под этим калькулятором приводит верную
 * формулу «(A+DBL)/2 − монокулярный PD» — реализация ей не соответствовала.
 * Здесь соглашение одно (монокулярное), бинокулярные вызовы делят PD пополам —
 * их числа не изменились.
 */
export function decentrationPerEye(aMm: number, dblMm: number, monocularPdMm: number): number {
  return (aMm + dblMm) / 2 - monocularPdMm;
}

/** Индуцированная призма по правилу Прентиса: Δ = |F · dec(мм)| / 10. */
export function prismByPrentice(powerD: number, decentrationMm: number): number {
  return Math.abs(powerD * decentrationMm) / 10;
}

/** Сферический эквивалент: SE = sph + cyl / 2. */
export function sphericalEquivalent(sphere: number, cyl: number): number {
  return sphere + cyl / 2;
}

/** Рецепт → оптический вектор (M, J0, J45). */
export function toVector({ sphere, cyl, axis }: Rx): PowerVector {
  const M = sphericalEquivalent(sphere, cyl);
  const rad = (axis * Math.PI) / 180;
  return {
    M,
    J0: (-cyl / 2) * Math.cos(2 * rad),
    J45: (-cyl / 2) * Math.sin(2 * rad),
  };
}

/**
 * Оптический вектор → рецепт (всегда в минусовом цилиндре).
 *
 * Вырожденный случай J0 = J45 = 0 (цилиндра нет): ось смысла не имеет, но поле
 * заполнить надо — отдаём 180°. Отдельная ветка нужна не для красоты:
 * `atan2` различает +0 и −0, и в прототипе такой рецепт получал то 90°, то
 * 180° в зависимости от знака нуля во входных данных.
 */
export function fromVector({ M, J0, J45 }: PowerVector): Rx {
  const cyl = -2 * Math.sqrt(J0 * J0 + J45 * J45);
  if (J0 === 0 && J45 === 0) {
    return { sphere: M, cyl: 0, axis: 180 };
  }
  let axis = (0.5 * Math.atan2(J45, J0) * 180) / Math.PI;
  if (axis < 0) axis += 180;
  if (axis <= 0) axis = 180;
  return { sphere: M - cyl / 2, cyl, axis: Math.round(axis) };
}

/**
 * Вес линзы, г: объём приближается цилиндром средней толщины (как в прототипе),
 * вес = объём (см³) × плотность (г/см³).
 */
export function volumeWeight(
  diameterMm: number,
  averageThicknessMm: number,
  density: number,
): number {
  const radiusCm = diameterMm / 2 / 10;
  const thicknessCm = averageThicknessMm / 10;
  return Math.PI * radiusCm * radiusCm * thicknessCm * density;
}
